import RecordRTC from 'recordrtc';
import { EnvironmentTypes } from '@common/constants';
import { RecordingQuality, SupportedMimeType, RecordingType } from '../types/media.enums';
import { isChromeOnIOS, isIOSSafari } from './browser-detection';
import { getBestSupportedMimeType } from '../config/mime-types';

/**
 * Get optimized recording options for RecordRTC based on device and quality
 */
export const createRecordingOptions = (
  quality: RecordingQuality = RecordingQuality.MEDIUM,
  enableAudio: boolean = true,
): any => {
  // Use the existing MIME type detection that handles Firefox correctly
  const mimeType = getBestSupportedMimeType(RecordingType.VIDEO) || SupportedMimeType.VIDEO_WEBM;
  const isIOS = isIOSSafari() || isChromeOnIOS();

  const baseOptions = {
    type: 'video',
    mimeType,
    recorderType: RecordRTC.MediaStreamRecorder,
    numberOfAudioChannels: enableAudio ? 1 : 0,
    desiredSampRate: 44100,
    timeSlice: 1000,
    // Disable RecordRTC console logs in production
    disableLogs: process.env.NODE_ENV !== EnvironmentTypes.DEVELOPMENT,
  };

  // Higher quality settings for better video recording
  let videoSettings: any;
  let bitrate: number;

  switch (quality) {
    case RecordingQuality.LOW:
      videoSettings = { width: 640, height: 480, frameRate: isIOS ? 15 : 20 };
      bitrate = isIOS ? 128000 : 256000;
      break;
    case RecordingQuality.HIGH:
      videoSettings = { width: 1280, height: 720, frameRate: isIOS ? 24 : 30 };
      bitrate = isIOS ? 512000 : 1000000;
      break;
    case RecordingQuality.ULTRA:
      videoSettings = { width: 1920, height: 1080, frameRate: isIOS ? 24 : 30 };
      bitrate = isIOS ? 1000000 : 2000000;
      break;
    default: // MEDIUM - good balance of quality and performance
      videoSettings = { width: 1280, height: 720, frameRate: isIOS ? 20 : 25 };
      bitrate = isIOS ? 256000 : 512000;
  }

  return {
    ...baseOptions,
    video: videoSettings,
    videoBitsPerSecond: bitrate,
  };
};
